// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { useEffect, useRef } from 'react';
import clsx from 'clsx';

import { findUpUntil } from '@amzn/awsui-component-toolkit/dom';
import { useMergeRefs, useStableCallback } from '@amzn/awsui-component-toolkit/internal';

import { BaseComponentProps } from '../../../types/base-component';
import { DropdownStatusProps } from '../../../types/dropdown-status';
import { BaseKeyDetail, CancelableEventHandler, NonCancelableEventHandler } from '../../../types/events';
import { getBaseProps } from '../../base-component';
import { fireKeyboardEvent, fireNonCancelableEvent } from '../../events';

import styles from './styles.css.js';

export interface OptionsListProps extends BaseComponentProps {
  open?: boolean;
  statusType: DropdownStatusProps.StatusType;
  /**
   * Options list
   */
  children: React.ReactNode;
  nativeAttributes?: Record<string, any>;
  /**
   * Called when more items need to be loaded.
   */
  onLoadMore?: NonCancelableEventHandler;
  onKeyDown?: CancelableEventHandler<BaseKeyDetail>;
  onBlur?: NonCancelableEventHandler<{ relatedTarget: Node | null }>;
  onFocus?: NonCancelableEventHandler;
  onMouseUp?: (itemIndex: number) => void;
  onMouseMove?: (itemIndex: number) => void;
  position?: React.CSSProperties['position'];
  tagOverride?: 'div' | 'ul';
  role?: 'listbox' | 'list' | 'menu';
  ariaLabel?: string;
  ariaLabelledby?: string;
  ariaDescribedby?: string;
  ariaRequired?: boolean;
  decreaseBlockMargin?: boolean;
  embedded?: boolean;
  stickyItemBlockSize?: number | null;
  isMultiSelect?: boolean;
  /** Internal: custom CSS properties set on the list root; inherited by the option rows. */
  customProperties?: React.CSSProperties;
}

const BOTTOM_TRIGGER_OFFSET = 80;

const getItemIndex = (containerRef: React.RefObject<HTMLElement>, event: React.MouseEvent) => {
  const target = findUpUntil(
    event.target as HTMLElement,
    element => element === containerRef.current || !!element.dataset.mouseTarget
  );
  const mouseTarget = target?.dataset.mouseTarget;
  return mouseTarget ? parseInt(mouseTarget) : -1;
};

const OptionsList = (
  {
    open,
    statusType,
    children,
    nativeAttributes = {},
    onKeyDown,
    onBlur,
    onFocus,
    onLoadMore,
    onMouseUp,
    onMouseMove,
    position = 'relative',
    role = 'listbox',
    tagOverride: Tag = 'div',
    decreaseBlockMargin = false,
    ariaLabel,
    ariaLabelledby,
    ariaDescribedby,
    ariaRequired,
    embedded,
    stickyItemBlockSize,
    isMultiSelect,
    customProperties,
    ...restProps
  }: OptionsListProps,
  ref: React.Ref<HTMLDivElement>
) => {
  const baseProps = getBaseProps(restProps);
  const menuRef = useRef<HTMLElement>(null);

  const handleScroll = useStableCallback(() => {
    const scrollContainer = menuRef?.current;
    if (scrollContainer) {
      const bottomEdgePosition = scrollContainer.scrollTop + scrollContainer.clientHeight;
      const remainingScrollHeight = scrollContainer.scrollHeight - bottomEdgePosition;
      if (remainingScrollHeight < BOTTOM_TRIGGER_OFFSET) {
        fireNonCancelableEvent(onLoadMore);
      }
    }
  });

  useEffect(() => {
    if (open && statusType === 'pending') {
      handleScroll();
    }
  }, [open, statusType, handleScroll]);

  const className = clsx(styles['options-list'], {
    [styles['decrease-block-margin']]: decreaseBlockMargin,
    [styles['options-list-embedded']]: embedded,
  });

  const mergedRef = useMergeRefs(ref, menuRef);

  return (
    <Tag
      {...baseProps}
      {...nativeAttributes}
      className={className}
      ref={mergedRef}
      style={{ ...customProperties, position, scrollPaddingBlockStart: stickyItemBlockSize ?? undefined }}
      role={role}
      onScroll={handleScroll}
      onKeyDown={event => fireKeyboardEvent(onKeyDown, event)}
      onMouseMove={event => onMouseMove?.(getItemIndex(menuRef, event))}
      onMouseUp={event => onMouseUp?.(getItemIndex(menuRef, event))}
      onBlur={event => fireNonCancelableEvent(onBlur, { relatedTarget: event.relatedTarget })}
      onFocus={() => fireNonCancelableEvent(onFocus)}
      tabIndex={embedded ? 0 : -1}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledby}
      aria-describedby={ariaDescribedby}
      aria-multiselectable={role === 'listbox' && isMultiSelect ? true : undefined}
      aria-required={ariaRequired}
    >
      {open && children}
    </Tag>
  );
};

export default React.forwardRef(OptionsList);



// WEBPACK FOOTER //
// /src/internal/components/options-list/index.tsx